import { gql } from "@urql/core";
import { ParticipantSurveyResponsesDocument } from "../../../generated/graphql";
import { useContext } from "react";
import { SurveyContext } from "../../survey";
import { AppContext } from "../../../app";
import { useQuery } from "urql";
import { SurveyAudienceContext } from "../../survey_page";

gql`
  query ParticipantSurveyResponses($userId: ID!) {
    user(id: $userId) {
      id
      surveyResponses {
        id
        context {
          participantId
          mentorshipChatGroupId
          mentorshipId
        }
      }
    }
  }
`;

export default function useRotatingQuestionIndex({
  numberOfQuestions,
  // This is an ugly work around so useSurveyQuestion can use this hook to determine the correct question index for
  // rotating questions. The error notification component is rendered outside of the SurveyContext provider (because not all instances
  // of surveys need buttons, errors, or are even submittable, such as the case of previews.). So when useSurveyQuestion calls this hook,
  // useRotatingQuestion doesn't know what the context of the survey is (since it is called outside of the context of the survey context provider).
  // We manually pass it in here so we can determine the correct question index and render the right prompt for the error message.
  // TODO: ripe for refactor of some kind
  surveyAudienceContext,
  pause,
}: {
  numberOfQuestions: number;
  surveyAudienceContext?: SurveyAudienceContext;
  pause?: boolean;
}): { fetching: boolean; questionIndex?: number } {
  const { userId } = useContext(AppContext);
  const currentSurveyContext = useContext(SurveyContext);
  const surveyId = currentSurveyContext.surveyId;
  const participantId = currentSurveyContext.participantId;
  const [{ fetching, error, data }] = useQuery({
    query: ParticipantSurveyResponsesDocument,
    variables: { userId },
    pause,
  });
  if (fetching || data === null) return { fetching: true, questionIndex: undefined };
  if (pause) {
    return { fetching: false, questionIndex: undefined };
  }
  if (error || !data) {
    throw new Error(
      `Error determining rotating survey question for survey:${surveyId}, participant${participantId}`
    );
  }

  const contextToUse = surveyAudienceContext ?? currentSurveyContext;

  const responses = data.user.surveyResponses.filter((response) => {
    // we need to make sure the context of this response is applicable. Currently this only includes
    // mentorships and mentorship chat groups. if we end up adding more context types w/ rotating
    // questions, this will need to be updated
    if (contextToUse.mentorshipChatGroupId) {
      if (
        !response.context.mentorshipChatGroupId ||
        // this isn't ideal but graphql ids are strings so we need to make sure whatever we got from rails is a string
        // (since the context object is untyped json)
        `${response.context.mentorshipChatGroupId}` !== contextToUse.mentorshipChatGroupId
      ) {
        return false;
      }
    }
    if (contextToUse.mentorshipId) {
      if (
        !response.context.mentorshipId ||
        // this isn't ideal but graphql ids are strings so we need to make sure whatever we got from rails is a string
        // (since the context object is untyped json)
        `${response.context.mentorshipId}` !== contextToUse.mentorshipId
      ) {
        return false;
      }
    }
    if (contextToUse.participantId) {
      if (
        !response.context.participantId ||
        // this isn't ideal but graphql ids are strings so we need to make sure whatever we got from rails is a string
        // (since the context object is untyped json)
        `${response.context.participantId}` !== contextToUse.participantId
      ) {
        return false;
      }
    }
    return true;
  });
  const numberOfResponses = responses.length;
  const questionIndex = numberOfResponses % numberOfQuestions;
  return { fetching: false, questionIndex };
}
